import React from 'react';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  Table,
  Text,
  Tooltip,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { IconAlertTriangle, IconCurrencyDollar } from '@tabler/icons-react';
import { Claim, Debit } from 'interfaces';
import { buttonTextColor } from 'utils/buttonUtils';

type ClaimTariff = Claim['tariffs'][number];
type ReturnedDebit = Omit<Debit, 'tariff'> & { tariff?: ClaimTariff };

export interface UnpaidUnmatchedCardProps {
  unpaidRequestedTariffs: ClaimTariff[];
  unmatchedDebits: ReturnedDebit[];
  selectedTariffIDs: string[];
  setSelectedTariffIDs: (ids: string[]) => void;
  selectedTariffs: ClaimTariff[];
  loading: boolean;
  onQueryTariffs: (tariffs: ClaimTariff[]) => void;
  onAcceptTariffs: (tariffs: ClaimTariff[]) => void;
  isAcceptDisabled: boolean;
  canCreateQuery: boolean;
}

type ComparisonRow =
  | {
      kind: 'submitted';
      key: string;
      tariff?: ClaimTariff;
    }
  | {
      kind: 'returned';
      key: string;
      debit: ReturnedDebit;
      tariff?: ClaimTariff;
      paidAmount?: string | number | null;
    };

const formatMoney = (value: string | number | null | undefined) => {
  const numericValue = Number(value ?? 0);
  if (!Number.isFinite(numericValue)) return '0.00';
  return numericValue.toFixed(2);
};

const renderEOBs = (item: ComparisonRow) => {
  if (item.kind !== 'returned' || !item.debit.eobs?.length) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <Group gap={4} wrap="wrap">
      {item.debit.eobs.map((eob) => (
        <Tooltip
          key={eob.id ?? eob.code}
          label={eob.description?.toLowerCase()}
          disabled={!eob.description}
          position="top"
          withArrow
          multiline
          maw={320}
        >
          <Badge variant="light" color="red" radius="sm">
            {eob.code}
          </Badge>
        </Tooltip>
      ))}
    </Group>
  );
};

export const UnpaidUnmatchedCard: React.FC<UnpaidUnmatchedCardProps> = ({
  unpaidRequestedTariffs,
  unmatchedDebits,
  selectedTariffIDs,
  setSelectedTariffIDs,
  selectedTariffs,
  loading,
  onQueryTariffs,
  onAcceptTariffs,
  isAcceptDisabled,
  canCreateQuery,
}) => {
  const theme = useMantineTheme();
  const mode = useComputedColorScheme();

  const rows: ComparisonRow[] = [
    ...unpaidRequestedTariffs.map((tariff) => ({
      kind: 'submitted' as const,
      key: `submitted-${tariff.id}`,
      tariff,
    })),
    ...unmatchedDebits.map((debit) => ({
      kind: 'returned' as const,
      key: `returned-${debit.id}-${debit.tariffID}`,
      debit,
      tariff: debit.tariff,
      paidAmount: debit.paidAmount,
    })),
  ];
  const selectedTariffIDSet = new Set(selectedTariffIDs);
  const toggleReturnedTariff = (id: string) => {
    setSelectedTariffIDs(
      selectedTariffIDSet.has(id)
        ? selectedTariffIDs.filter((selectedID) => selectedID !== id)
        : [...selectedTariffIDs, id]
    );
  };

  return (
    <Card withBorder shadow="sm" radius="md" p="md" mt="md">
      <Group justify="space-between" mb="xs">
        <Badge color="orange" leftSection={<IconAlertTriangle size={14} />}>
          Assessment Mismatch
        </Badge>
      </Group>

      <Table
        horizontalSpacing="md"
        verticalSpacing="xs"
        withTableBorder
        withColumnBorders
        striped
        highlightOnHover
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th ta="center" style={{ width: 72 }}>
              Match
            </Table.Th>
            <Table.Th>Code</Table.Th>
            <Table.Th>Description</Table.Th>
            <Table.Th>EOB</Table.Th>
            <Table.Th ta="right">Rate</Table.Th>
            <Table.Th ta="right">Units</Table.Th>
            <Table.Th ta="right">Paid</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((item) => (
            <Table.Tr
              key={item.key}
              onClick={() => {
                if (item.kind === 'returned') {
                  toggleReturnedTariff(item.tariff?.id ?? item.debit.tariffID);
                }
              }}
              style={{
                cursor: item.kind === 'returned' ? 'pointer' : 'default',
                background:
                  item.kind === 'returned' &&
                  selectedTariffIDSet.has(
                    item.tariff?.id ?? item.debit.tariffID
                  )
                    ? mode === 'dark'
                      ? theme.colors.dark[6]
                      : theme.colors.gray[1]
                    : undefined,
              }}
            >
              <Table.Td
                ta="center"
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                {item.kind === 'returned' ? (
                  <Checkbox
                    size="xs"
                    checked={selectedTariffIDSet.has(
                      item.tariff?.id ?? item.debit.tariffID
                    )}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => {
                      toggleReturnedTariff(
                        item.tariff?.id ?? item.debit.tariffID
                      );
                    }}
                    aria-label={`Select returned tariff ${
                      item.tariff?.code ?? item.debit.tariffID
                    }`}
                  />
                ) : null}
              </Table.Td>
              <Table.Td>
                <Text fw={700} size="sm" style={{ whiteSpace: 'nowrap' }}>
                  {item.tariff
                    ? `${item.tariff.prefix ?? ''}${item.tariff.code}`
                    : item.kind === 'returned'
                    ? item.debit.tariffID
                    : ''}
                </Text>
              </Table.Td>
              <Table.Td style={{ maxWidth: 360 }}>
                <Text
                  size="sm"
                  c="dimmed"
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.tariff?.description ?? 'Tariff details unavailable'}
                </Text>
              </Table.Td>
              <Table.Td>{renderEOBs(item)}</Table.Td>
              <Table.Td>
                <Group gap={6} justify="flex-end">
                  <IconCurrencyDollar size={16} />
                  <Text size="sm">{formatMoney(item.tariff?.rate)}</Text>
                </Group>
              </Table.Td>
              <Table.Td>
                <Text size="sm" ta="right">
                  {item.tariff?.units ?? 1}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm" ta="right">
                  {item.kind === 'returned' ? formatMoney(item.paidAmount) : ''}
                </Text>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <Group justify="flex-end" mt="md" gap="xs">
        <Button
          size="sm"
          variant="outline"
          disabled={selectedTariffs.length === 0 || loading || !canCreateQuery}
          c={buttonTextColor(
            mode,
            selectedTariffs.length === 0 || loading || !canCreateQuery
          )}
          onClick={() => onQueryTariffs(selectedTariffs)}
        >
          Query
        </Button>
        <Button
          size="sm"
          disabled={isAcceptDisabled}
          loading={loading}
          onClick={() => {
            if (selectedTariffs.length === 0) return;
            onAcceptTariffs(selectedTariffs);
          }}
          c={buttonTextColor(mode, isAcceptDisabled)}
        >
          Match
        </Button>
      </Group>
    </Card>
  );
};
